import { useEffect, useRef, useState } from "react";
import { useTypedSelector } from "hooks";
import { when } from "util/support";
import { WebRoutes } from "consts/enpoints/web";
import type { Merchant as MerchantType } from "features/ecommerce/types";
import { Merchant as MerchantApi } from "features/ecommerce/api";
import { selectOnboardingLegalFormOptions } from "features/general/selectors";
import { Merchant } from "ui/forms/ECommerce";
import { useOnboardingForm, useOnboardingStepData } from "ui/organizms/ECommerce/Onboarding/common";
import { useOnboardingApplication } from "../../hooks";
import { useDeleteIdentities, useUpdateOrCreateIdentities } from "./ubo";

export const useUBOIdentityVerification = () => {
    const { getApplicationModel } = useOnboardingApplication();
    const legalFormOptions = useTypedSelector(selectOnboardingLegalFormOptions);
    const { data } = useOnboardingStepData<MerchantType.OnboardingIdentityVerification>();

    const { handleDeleteIdentities } = useDeleteIdentities();
    const { handleUpdateOrCreateIdentities } = useUpdateOrCreateIdentities();

    const withValues = useOnboardingForm({
        save: MerchantApi.putOnboardingsIdentityVerificationSteps,
        route: WebRoutes.Onboarding.Merchant,
        getApplicationModel
    });

    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const async = (...args: Parameters<typeof setTimeout>) => {
        clearTimeout(timeoutRef.current);

        timeoutRef.current = setTimeout(...args);
    };

    const [tab, setTab] = useState('1');
    const [uboIdentities, setUboIdentities] = useState<Array<Merchant.UBOIdentityVerification.Identity>>(() => (
        when(
            data.uboIdentities.length,
            data.uboIdentities,
            [{}]
        )
            .map((identity: MerchantType.UboIdentity) => new Merchant.UBOIdentityVerification.Identity(identity))
    ));
    const uboIdentitiesRef = useRef(uboIdentities);
    uboIdentitiesRef.current = uboIdentities;

    const fromTab = (tabIndex: number | string) => Number(tabIndex) - 1;

    const formProps: Merchant.UBOIdentityVerification.OnboardingIdentityVerificationForm = {
        isBootstrapDataLoading: false,
        bootstrapData: {
            legalForm: legalFormOptions
        },
        initialValues: Merchant.UBOIdentityVerification.getInitialValues(data.uboIdentities?.[0]),
        onSaveOrCreate: async (values, formHelpers) => {
            // Replace the current identity from the identities list with fresh form values
            const identities = uboIdentities.map((identity, index) => (
                when(
                    Object.is(index, fromTab(tab)),
                    values,
                    identity
                )
            ));

            return handleDeleteIdentities(identities)
                .then(() => handleUpdateOrCreateIdentities(identities))
                .then(() => withValues({}, formHelpers));
        }
    };

    const identityVerification = Merchant.UBOIdentityVerification.useForm(formProps);

    const resetForm = (tabIndex: number | string, identities: Array<Merchant.UBOIdentityVerification.Identity>) => {
        identityVerification.resetForm({
            values: identities[fromTab(tabIndex)],
            errors: {},
            touched: {}
        });

        setTab(`${tabIndex}`);
    };

    const onIdentityChange = (tabIndex: number | string) => {
        const prevTab = tab;
        setUboIdentities(prev => prev.map((identity, index) => {
            if (Object.is(index, fromTab(prevTab))) {
                return identityVerification.values;
            }

            return identity;
        }));

        resetForm(tabIndex, uboIdentitiesRef.current);

        async(() => identityVerification.validateForm());
    };

    const handleAddUbo = () => {
        uboIdentitiesRef.current = [
            ...uboIdentities,
            new Merchant.UBOIdentityVerification.Identity()
        ];

        setUboIdentities(uboIdentitiesRef.current);

        async(() => onIdentityChange(uboIdentities.length + 1), 100);
    };

    const handleRemoveUbo = (index: number) => () => {
        const identities = uboIdentities.filter((_, key) => !Object.is(key, index));

        setUboIdentities(identities);

        async(() => (
            resetForm(
                Math.max(identities.length, 1),
                identities
            )
        ), 100);
    };

    useEffect(() => () => clearTimeout(timeoutRef.current), []);

    return {
        ...formProps,
        ...identityVerification,
        ...data,
        tab,
        uboIdentities,
        onIdentityChange,
        handleAddUbo,
        handleRemoveUbo
    };
};

export default useUBOIdentityVerification;
